import React, { useEffect } from 'lib/react';
import {
  EVAlert, EVTypography, makeStyles, EVCheckCircleOutlineIcon,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import { Link } from 'lib/router';
import clsx from 'lib/clsx';
import { SUCCESS, ERROR } from 'constants/AppConstants';
import { SUPPORT_ROUTE } from 'constants/RouteConstants';
import { useDispatch, useSelector } from 'lib/redux';
import { closeCancelOrderAlert } from 'features/cancel-order-dialog/CancelOrder.actions';

const useStyles = makeStyles((theme) => ({
  successAlert: {
    backgroundColor: theme.evPalette.lime[100],
    borderBottom: `1px solid ${theme.evPalette.lime[700]}`,
  },
  errorAlert: {
    backgroundColor: theme.evPalette.rose[100],
    borderBottom: `1px solid ${theme.evPalette.rose[900]}`,
  },
  alert: {
    padding: '8px 16px 8px 16px',
    borderRadius: '1px',
    '@media (min-width: 640px)': {
      margin: '16px 24px 16px 24px',
      padding: '16px',
      border: 'none',
      borderRadius: '4px',
    },
  },
  alertMsg: {
    display: 'flex',
    flexDirection: 'column',
    color: theme.evPalette.charcoal[900],
    gap: '8px',
    fontSize: '14px',
    '&>.title': {
      fontWeight: 600,
      lineHeight: '19px',
    },
    '&>.message': {
      fontWeight: 400,
      lineHeight: '18px',
      '&>a': {
        color: theme.evPalette.ocean[700],
      },
    },
    '@media (min-width: 640px)': {
      gap: '16px',
      '&>.title': {
        fontWeight: 600,
        fontSize: '18px',
        lineHeight: '27px',
      },
      '&>.message': {
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '24px',
      },
    },
  },
  successAlertIcon: { '& svg': { color: theme.evPalette.lime[700] } },
  errorAlertIcon: { '& svg': { color: theme.evPalette.rose[900] } },
  closeBtn: { alignItems: 'flex-start' },
}));

function CancelOrderAlert() {
  const { t } = useTranslation();
  const classes = useStyles();

  const cancelOrderStatus = useSelector(({ cancelOrder }) => cancelOrder.cancelOrderStatus);
  const dispatch = useDispatch();

  const onClose = () => dispatch(closeCancelOrderAlert());
  useEffect(() => () => dispatch(closeCancelOrderAlert()), []);

  if (cancelOrderStatus === SUCCESS) {
    return (
      <EVAlert
        severity={SUCCESS}
        onClose={onClose}
        className={classes.alert}
        classes={{
          icon: classes.successAlertIcon, message: classes.alertMsg, root: classes.successAlert, action: clsx(classes.closeBtn, classes.successAlertIcon),
        }}
        iconMapping={{ success: <EVCheckCircleOutlineIcon /> }}
      >
        <EVTypography className="title">{t('order_history__actions__cancel_order_success_title')}</EVTypography>
        <EVTypography className="message">
          {t('order_history__actions__cancel_order_success_message')}
          <Link to={SUPPORT_ROUTE}>{t('order_history__actions__customer_service_link')}</Link>
          .
        </EVTypography>
      </EVAlert>
    );
  }
  if (cancelOrderStatus === ERROR) {
    return (
      <EVAlert
        severity={ERROR}
        onClose={onClose}
        className={classes.alert}
        classes={{
          icon: classes.errorAlertIcon, message: classes.alertMsg, root: classes.errorAlert, action: clsx(classes.closeBtn, classes.errorAlertIcon),
        }}
      >
        <EVTypography className="title">{t('order_history__actions__cancel_order_error_title')}</EVTypography>
        <EVTypography className="message">
          {t('order_history__actions__cancel_order_error_message')}
          <Link to={SUPPORT_ROUTE}>{t('order_history__actions__customer_service_link')}</Link>
          .
        </EVTypography>
      </EVAlert>
    );
  }

  return null;
}

export default CancelOrderAlert;
